import { Link } from 'react-router-dom';
import { TrendingDown, CheckCircle2 } from 'lucide-react';

interface DebtData {
  id: number;
  name: string;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  progress: number;
}

interface DebtSummaryProps {
  debts: DebtData[];
  totalBalance: number;
  totalMinPayment: number;
  isLoading?: boolean;
}

export default function DebtSummary({ debts, totalBalance, totalMinPayment, isLoading }: DebtSummaryProps) {
  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Debt Overview</h3>
            <p className="text-sm text-text-muted">Active debts</p>
          </div>
        </div>
        <div className="pt-6 space-y-5">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between mb-2">
                <div className="h-4 bg-background-subtle rounded w-1/2" />
                <div className="h-4 bg-background-subtle rounded w-20" />
              </div>
              <div className="h-2 bg-background-subtle rounded" />
              <div className="mt-2 h-3 bg-background-subtle rounded w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (debts.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Debt Overview</h3>
            <p className="text-sm text-text-muted">Active debts</p>
          </div>
        </div>
        <div className="pt-10 pb-8 text-center">
          <div className="mx-auto h-12 w-12 bg-success-bg rounded-full flex items-center justify-center mb-4 border border-success/20">
            <CheckCircle2 className="h-6 w-6 text-success" />
          </div>
          <p className="text-sm text-text-muted">No debts to track — you’re debt free.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between pb-5 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-text-header">Debt Overview</h3>
          <p className="text-sm text-text-muted">Active debts</p>
        </div>
        <Link to="/debts" className="text-sm font-medium text-primary hover:text-primary-hover transition-colors">
          View all
        </Link>
      </div>

      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 p-4 rounded-xl border border-border bg-background-subtle/30">
        <div>
          <p className="text-xs text-text-muted">Total Balance</p>
          <p className="text-lg font-semibold text-text-header">{formatCurrency(totalBalance)}</p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Min. Monthly</p>
          <p className="text-lg font-semibold text-text-header">{formatCurrency(totalMinPayment)}</p>
        </div>
      </div>

      {/* Debt list */}
      <div className="mt-6 space-y-5">
        {debts.map((debt) => {
          const progress = Math.min(Math.max(debt.progress, 0), 100);
          return (
            <div
              key={debt.id}
              className="p-3 rounded-lg border border-transparent hover:border-border hover:bg-background-subtle/40 transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-header truncate">{debt.name}</p>
                  <div className="mt-1 inline-flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {debt.interest_rate}% APR
                    </span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-text-header">{formatCurrency(debt.current_balance)}</p>
              </div>

              <div className="w-full bg-background-subtle rounded-full h-2 overflow-hidden border border-border">
                <div
                  className="h-2 rounded-full bg-primary transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-text-muted mt-2">{progress.toFixed(0)}% paid off</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <Link to="/debts" className="btn btn-secondary w-full">
          <TrendingDown className="w-4 h-4" />
          Open debt planner
        </Link>
      </div>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
